import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnMapQueryParams,
  ISnMapResponse,
  ISnListQueryParameters,
  ISnListResponse,
} from '@lib/api';
import { FsRegistry, FsRegistryImport } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class FsRegistryAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getAllRegistry(params?: ISnListQueryParameters): Observable<ISnListResponse<FsRegistry>> {
    return this._httpClient.get('/api/registry/', { params }).pipe(
      tap({
        error: () =>
          this._toastrService.error(t('registry.errors.retrieveFailed')),
      }),
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((res) => new FsRegistry(res)),
      })),
    );
  }

  public deleteRegistry(id: string): any {
    return this._dialogService
      .confirm({
        message: t('registry.confirmations.deleteRegistry'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/registry/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('registry.success.deleted')),
          error: () =>
            this._toastrService.error(t('registry.errors.deleteFailed')),
        }),
      );
  }

  public getRegistry(id: string): Observable<any> {
    return this._httpClient.get(`/api/registry/${id}/`);
  }

  public updateRegistry(id: string, data: any): Observable<FsRegistry> {
    return this._httpClient.patch(`/api/registry/${id}/`, data).pipe(
      map((response: any) => new FsRegistry(response)),
      tap({
        next: () => this._toastrService.success(t('registry.success.updated')),
        error: () =>
          this._toastrService.error(t('registry.errors.updateFailed')),
      }),
    );
  }

  public getRegistryLocation(
    params: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<FsRegistry>> {
    return this._httpClient.get('/api/maps/static/registry/', { params }).pipe(
      catchError(() => of<ISnMapResponse<FsRegistry>>({ results: [], clusterResults: [] })),
      map((response: any) => {
        return {
          results: response.results.map((registry) => new FsRegistry(registry)),
          clusterResults: response.clusterResults || [],
        };
      }),
    );
  }

  public importCSVFile(data: { description: string; filename: File }): Observable<any> {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('filename', data.filename);
    return this._httpClient.post('/api/registry/import/', formData).pipe(
      tap({
        next: () => {
          this._toastrService.success(t('registry.success.csvImported'));
          return this.returnCSVFiles();
        },
        error: () => this._toastrService.error(t('registry.errors.csvImportFailed')),
      }),
    );
  }

  public returnCSVFiles(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsRegistryImport>> {
    return this._httpClient.get('/api/registry/import/', { params }).pipe(
      map((response: any) => {
        return {
          count: response.count,
          totalPages: response.totalPages ?? 1,
          results: response.results.map((registry) => new FsRegistryImport(registry)),
        };
      }),
    );
  }

  public downloadLink(): Observable<any> {
    return this._httpClient
      .get('/api/registry/import/', {
        headers: {
          'Content-Type': 'text/csv',
        },
        responseType: 'blob',
      })
      .pipe(
        tap({
          next: (res) => {
            const downloadLink = document.createElement('a');
            downloadLink.setAttribute('download', 'DownloadedFile.csv');
            const url = URL.createObjectURL(res);
            downloadLink.setAttribute('href', url);
            downloadLink.click();
            this._toastrService.success(t('common.downloadStarted'));
          },
          error: () => {
            this._toastrService.error(t('common.downloadFailed'));
          },
        }),
      );
  }

  public downloadBlobFile(): Observable<any> {
    return this._httpClient
      .get('/api/registry/import/sample/', {
        headers: {
          'Content-Type': 'text/csv',
        },
        responseType: 'blob',
      })
      .pipe(
        tap({
          next: (res) => {
            const downloadLink = document.createElement('a');
            downloadLink.setAttribute('download', 'registry-ex.csv');
            const url = URL.createObjectURL(res);
            downloadLink.setAttribute('href', url);
            downloadLink.click();
          },
          error: () => {
            this._toastrService.error(t('common.downloadFailed'));
          },
        }),
      );
  }

  public downloadRegistryMOU(id: string): Observable<any> {
    return this._httpClient
      .get(`/api/registry/${id}/mou/`, {
        headers: {
          'Content-Type': 'application/pdf',
        },
        responseType: 'blob',
      })
      .pipe(
        tap({
          next: (res) => {
            const exportToPDF = document.createElement('a');
            exportToPDF.setAttribute('download', 'registry-MOU.pdf');
            const url = URL.createObjectURL(res);
            exportToPDF.setAttribute('href', url);
            exportToPDF.click();
            this._toastrService.success(t('common.downloadStarted'));
          },
          error: () => {
            this._toastrService.error(t('common.downloadFailed'));
          },
        }),
      );
  }
}
